import { FC, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { unlockDynamicLink, fetchDynamicToken } from "../services/api";
import type { DynamicTokenResponse } from "../types";

type Phase = "loading" | "password" | "ready" | "error";

const formatCountdown = (totalSeconds: number): string => {
    const s = Math.max(0, totalSeconds);
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

const DynamicQrPage: FC = () => {
    const { publicId } = useParams<{ publicId: string }>();

    const [phase, setPhase] = useState<Phase>("loading");
    const [errorMsg, setErrorMsg] = useState<string>("");
    const [passwordInput, setPasswordInput] = useState<string>("");
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [tokenData, setTokenData] = useState<DynamicTokenResponse | null>(null);
    const [remaining, setRemaining] = useState<number>(0);

    // Segreti tenuti SOLO in memoria (mai in localStorage).
    const accessTokenRef = useRef<string | null>(null);
    const passwordRef = useRef<string | undefined>(undefined);
    const nextRotationRef = useRef<number>(0); // epoch ms
    const fetchingRef = useRef<boolean>(false);

    const doUnlock = async (password?: string) => {
        const res = await unlockDynamicLink(publicId as string, password);
        accessTokenRef.current = res.data.data.accessToken;
        passwordRef.current = password;
    };

    const loadToken = async () => {
        const attempt = async () => {
            const res = await fetchDynamicToken(publicId as string, accessTokenRef.current as string);
            return res.data.data;
        };

        let data: DynamicTokenResponse;
        try {
            data = await attempt();
        } catch (err: any) {
            // Token breve scaduto → ri-unlock con la password conservata in memoria.
            if (err?.response?.status === 401) {
                await doUnlock(passwordRef.current);
                data = await attempt();
            } else {
                throw err;
            }
        }

        setTokenData(data);
        const next = new Date(data.nextRotationAt).getTime();
        nextRotationRef.current = Number.isNaN(next)
            ? Date.now() + data.rotationSeconds * 1000
            : next;
        setRemaining(Math.max(0, Math.round((nextRotationRef.current - Date.now()) / 1000)));
    };

    // Avvio: tenta unlock senza password. Se il link è protetto → mostra il form.
    useEffect(() => {
        if (!publicId) {
            setErrorMsg("Link non valido.");
            setPhase("error");
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                await doUnlock(undefined);
                if (cancelled) return;
                await loadToken();
                if (cancelled) return;
                setPhase("ready");
            } catch (err: any) {
                if (cancelled) return;
                const status = err?.response?.status;
                const msg: string = err?.response?.data?.message || "";
                // Il backend risponde 400 sia quando serve la password sia per
                // link revocato/scaduto/rate-limit: distinguiamo dal messaggio.
                const needsPassword = /password/i.test(msg);
                if ((status === 400 && needsPassword) || status === 401 || status === 403) {
                    setPhase("password");
                } else {
                    setErrorMsg(
                        msg || "Questo link non è disponibile (revocato o scaduto).",
                    );
                    setPhase("error");
                }
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [publicId]);

    // Countdown + rifetch alla rotazione.
    useEffect(() => {
        if (phase !== "ready") return;
        const id = setInterval(() => {
            const rem = Math.max(0, Math.round((nextRotationRef.current - Date.now()) / 1000));
            setRemaining(rem);
            if (rem <= 0 && !fetchingRef.current) {
                fetchingRef.current = true;
                loadToken()
                    .catch((err: any) => {
                        setErrorMsg(
                            err?.response?.data?.message || "Impossibile aggiornare il codice.",
                        );
                        setPhase("error");
                    })
                    .finally(() => {
                        fetchingRef.current = false;
                    });
            }
        }, 1000);
        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase]);

    const handleUnlockSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        setErrorMsg("");
        try {
            await doUnlock(passwordInput);
            await loadToken();
            setPhase("ready");
        } catch (err: any) {
            setErrorMsg(
                err?.response?.data?.message || "Password errata o link non valido.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    const isWarning = phase === "ready" && remaining <= 30;

    return (
        <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-4">
            <div className="w-full max-w-sm bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden">
                <div className="p-5 border-b border-slate-700 bg-slate-900/50 text-center">
                    <h1 className="text-xl font-bold text-white">Codice di accesso</h1>
                    <p className="text-slate-400 text-sm mt-1">Mostra questo QR all'ingresso.</p>
                </div>

                {phase === "loading" && (
                    <div className="p-12 text-center text-slate-400">Caricamento...</div>
                )}

                {phase === "error" && (
                    <div className="p-8 text-center">
                        <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-red-500/10 flex items-center justify-center text-red-400">
                            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                        </div>
                        <p className="text-red-200">{errorMsg}</p>
                    </div>
                )}

                {phase === "password" && (
                    <form onSubmit={handleUnlockSubmit} className="p-6 space-y-4">
                        <p className="text-slate-300 text-sm">
                            Questo link è protetto. Inserisci la password per continuare.
                        </p>
                        <input
                            type="password"
                            value={passwordInput}
                            onChange={(e) => setPasswordInput(e.target.value)}
                            autoFocus
                            className="w-full bg-slate-900/50 border border-slate-700 text-white rounded-xl focus:ring-2 focus:ring-sky-500 block p-3"
                            placeholder="Password"
                        />
                        {errorMsg && <p className="text-red-400 text-sm">{errorMsg}</p>}
                        <button
                            type="submit"
                            disabled={submitting || !passwordInput}
                            className="w-full py-3 bg-gradient-to-r from-sky-600 to-indigo-600 hover:from-sky-500 hover:to-indigo-500 text-white font-bold rounded-xl disabled:opacity-50 transition-all"
                        >
                            {submitting ? "Sblocco..." : "Sblocca"}
                        </button>
                    </form>
                )}

                {phase === "ready" && tokenData && (
                    <div className="flex flex-col items-center">
                        <div className="p-8 bg-white flex justify-center items-center w-full">
                            <div className="p-2 border-2 border-slate-100 rounded-lg shadow-inner">
                                <QRCodeCanvas value={tokenData.code} size={240} level="M" />
                            </div>
                        </div>

                        <div
                            className={`w-full p-5 text-center transition-colors ${
                                isWarning ? "bg-red-500/10" : "bg-slate-800"
                            }`}
                        >
                            <p className="text-xs uppercase font-semibold tracking-wider text-slate-400">
                                Nuovo codice tra
                            </p>
                            <p
                                className={`text-4xl font-bold mt-1 tabular-nums ${
                                    isWarning ? "text-red-400 animate-pulse" : "text-white"
                                }`}
                            >
                                {formatCountdown(remaining)}
                            </p>
                            {isWarning && (
                                <p className="text-red-300 text-sm mt-2 font-medium">
                                    Il codice sta per cambiare. Attendi il nuovo QR se non sei ancora
                                    passato.
                                </p>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {phase === "ready" && (
                <p className="text-slate-500 text-xs mt-4 text-center max-w-sm">
                    Il codice si aggiorna automaticamente ogni {tokenData?.rotationSeconds ?? 0} secondi.
                    Tieni aperta questa pagina.
                </p>
            )}
        </div>
    );
};

export default DynamicQrPage;
